import React from 'react';
import type { EpicDetail } from '../../../../../../shared/types/epics';
import { CostEstimateCard } from '../../../../components/epicDetail/CostEstimateCard';
import { EfficiencyCard, Stat } from '../cost/EfficiencyCard';
import { epicSpend, formatCost } from '../format';

export const CostTab: React.FC<{
  epic: EpicDetail;
  buildCostUsd: number;
  reviewCostUsd: number;
}> = ({ epic, buildCostUsd, reviewCostUsd }) => {
  const spend = epicSpend(epic);
  // Listed runs can undercount (old sessions expire after 90 days); the
  // per-story lifetime totals don't. Show whichever is larger, and say so.
  const build = Math.max(buildCostUsd, spend.total);
  const partial = spend.total > buildCostUsd + 0.005;
  const rows = epic.stories
    .filter((s) => (s.lifetimeCostUsd || 0) > 0)
    .sort((a, b) => b.lifetimeCostUsd - a.lifetimeCostUsd);
  const max = rows.length > 0 ? rows[0].lifetimeCostUsd : 0;
  return (
  <div className="space-y-5">
    <CostEstimateCard costEstimate={epic.costEstimate} />
    <EfficiencyCard epic={epic} />
    {/* Two-phase cost split — same buildCostUsd / reviewCostUsd memos the page
        already computes from sessionsByStory / prWatches; just surfaced here
        instead of in the old "Build" / "Review" PhaseHeader meta text. */}
    <div className="grid grid-cols-2 gap-3" data-testid="epic-cost-split">
      <Stat label="Build cost (all runs)" value={formatCost(build)} />
      <Stat label="Review cost" value={formatCost(reviewCostUsd)} />
    </div>
    {partial && (
      <p className="text-xs text-slate-500" data-testid="cost-partial-note">
        Individual run history is incomplete (older runs expire), so this uses each story's recorded lifetime cost.
      </p>
    )}
    {rows.length > 0 && (
      <section className="rounded-xl border border-slate-200 bg-white px-4 py-3 space-y-2" data-testid="cost-by-story">
        <h2 className="text-sm font-semibold text-slate-800">Where the money went</h2>
        <ul className="space-y-1.5">
          {rows.map((s) => (
            <li key={s.id} className="text-xs">
              <div className="flex justify-between gap-3">
                <span className="truncate text-slate-700">{s.title || s.id}</span>
                <span className="tabular-nums text-slate-900">{formatCost(s.lifetimeCostUsd)}</span>
              </div>
              <div className="h-1.5 rounded bg-slate-100 mt-0.5">
                <div className="h-1.5 rounded bg-primary-500" style={{ width: `${Math.max(3, (s.lifetimeCostUsd / max) * 100)}%` }} />
              </div>
            </li>
          ))}
          {spend.setup > 0 && (
            <li className="text-xs flex justify-between text-slate-500">
              <span>Planning &amp; setup runs</span><span className="tabular-nums">{formatCost(spend.setup)}</span>
            </li>
          )}
        </ul>
      </section>
    )}
  </div>
  );
};
